import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, FolderOpen, Loader2, Search, X } from 'lucide-react';
import {
  choosePhotoFolder,
  chooseSearchFolder,
  folderPermission,
  PHOTO_FOLDER_NAME,
  photoFolderSupported,
  savedPhotoFolder,
  savedSearchFolder,
  searchFolder,
  type DirHandle,
} from '../../lib/claire/photoFolder';
import { ClaireService, type ClaireFolderListJob } from '../../services/claireService';
import { cn } from '../../utils/cn';

const errText = (e: any) => e?.message?.replace(/^.*?:\s*/, '') || 'Something went wrong.';

/** Searches a folder on this computer for file and folder names for Claire. Only matching names, sizes and dates are sent. */
export const FileSearchCard: React.FC<{ job: ClaireFolderListJob; compact?: boolean; onDone?: (text: string) => void }> = ({ job: initial, compact, onDone }) => {
  const [job, setJob] = useState(initial);
  const [folder, setFolder] = useState<DirHandle | null>(null);
  const [perm, setPerm] = useState<'granted' | 'denied' | 'prompt' | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const photoFolder = job.search?.where !== 'otherFolder';
  const query = job.search?.query || '';

  useEffect(() => {
    let live = true;
    (photoFolder ? savedPhotoFolder() : savedSearchFolder()).then(async (h) => {
      if (!live || !h) return;
      setFolder(h);
      setPerm(await folderPermission(h, false));
    });
    return () => {
      live = false;
    };
  }, [photoFolder]);

  const run = useCallback(
    async (root: DirHandle) => {
      started.current = true;
      setError(null);
      try {
        setProgress(`Searching “${root.name}”…`);
        const r = await searchFolder(root, query, job.search?.type || 'any', (n) => setProgress(`Searching “${root.name}”… ${n.toLocaleString()} items checked`));
        setProgress('Sending the matches to Claire…');
        const next = await ClaireService.folderList('searchResults', job.id, { ...r, folderName: root.name });
        setJob(next);
        setProgress(null);
        onDone?.(`The file search is done (${next.count ?? r.matches.length} matches). Go ahead.`);
      } catch (e) {
        setProgress(null);
        setError(errText(e));
        started.current = false;
      }
    },
    [job.id, job.search?.type, query, onDone],
  );

  useEffect(() => {
    if (job.status === 'waiting' && folder && perm === 'granted' && !started.current) run(folder);
  }, [job.status, folder, perm, run]);

  const connect = async () => {
    setError(null);
    try {
      const h = await (photoFolder ? choosePhotoFolder() : chooseSearchFolder());
      setFolder(h);
      setPerm('granted');
      run(h);
    } catch (e: any) {
      if (e?.name !== 'AbortError') setError(errText(e));
    }
  };

  const allow = async () => {
    if (!folder) return connect();
    const p = await folderPermission(folder, true).catch(() => 'denied' as const);
    setPerm(p);
    if (p === 'granted') run(folder);
  };

  const cancel = async () => {
    setError(null);
    try {
      setJob(await ClaireService.folderList('cancel', job.id));
    } catch (e) {
      setError(errText(e));
    }
  };

  const button = 'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-medium disabled:opacity-50';
  const primary = cn(button, 'bg-[#241813] text-white hover:bg-black');
  const secondary = cn(button, 'border border-[#e0d6c6] bg-white text-[#5a4a3a] hover:border-[#b8986e]');

  return (
    <div className={cn('mt-2.5 rounded-xl border border-[#e0d3bf] bg-[#fbf7f0] px-3.5 py-3', compact ? 'text-[12.5px]' : 'text-[13.5px]')}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9a8a78]">
        <Search className="w-3.5 h-3.5" /> Claire is searching for “{query}”
      </p>

      {job.status === 'waiting' && !progress && (
        <div className="mt-1.5 space-y-2 text-[#241813]">
          <p>
            {photoFolder ? `Searches every folder inside “${PHOTO_FOLDER_NAME}”.` : 'Choose the folder to search, like Desktop, Documents or Downloads; every folder inside it is searched.'} Only
            matching names are shared with Claire, never the files.
          </p>
          {!photoFolderSupported() ? (
            <p className="text-[12px] text-amber-800">This needs Chrome or Edge on a computer.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {!folder ? (
                <button onClick={connect} className={primary}>
                  <FolderOpen className="w-3.5 h-3.5" /> {photoFolder ? `Choose “${PHOTO_FOLDER_NAME}”` : 'Choose a folder'}
                </button>
              ) : perm !== 'granted' ? (
                <button onClick={allow} className={primary}>
                  <FolderOpen className="w-3.5 h-3.5" /> Allow access to “{folder.name}”
                </button>
              ) : (
                <button onClick={() => run(folder)} className={primary}>
                  <Search className="w-3.5 h-3.5" /> Search “{folder.name}”
                </button>
              )}
              {folder && (
                <button onClick={connect} className={secondary}>
                  Use a different folder
                </button>
              )}
              <button onClick={cancel} className={secondary}>
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {progress && (
        <p className="mt-2 flex items-center gap-2 text-[#5a4a3a]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress}
        </p>
      )}

      {job.status === 'done' && (
        <p className="mt-1.5 flex items-center gap-1.5 text-emerald-700">
          <CheckCircle2 className="w-3.5 h-3.5" /> Sent Claire {job.count ?? 0} match{job.count === 1 ? '' : 'es'} from “{job.folderName || 'the folder'}”.
        </p>
      )}
      {job.status === 'cancelled' && <p className="mt-1.5 text-[#8a7a6a]">Cancelled.</p>}
      {error && <p className="mt-1.5 text-[12px] text-red-700">{error}</p>}
    </div>
  );
};
